import { useEffect, useState } from "react";
import { api, fileUrl, formatIDR } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Package, X, Check, Lock, Eye, Calculator, TrendingUp } from "lucide-react";
import MultiImageUploader from "@/components/MultiImageUploader";

const empty = { name: "", description: "", price: "", country: "", photos: [], fee_type: "fixed", fee_value: "", hide_price: false };

export default function MyCatalog() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(empty);
  const [countries, setCountries] = useState([]);
  // Iter 22 — quick country filter ("" = all)
  const [countryFilter, setCountryFilter] = useState("");
  // Iter 22 — Profit Calculator (local only, NEVER saved to backend — jastiper's dapur rahasia)
  const [showProfitCalc, setShowProfitCalc] = useState(false);
  const [costPrice, setCostPrice] = useState("");
  // Live platform fee fetched from admin settings (so calc reflects current cut)
  const [jastiperFeePct, setJastiperFeePct] = useState(0);

  useEffect(() => {
    load();
    api.get("/countries").then((r) => setCountries(r.data));
    api.get("/fee-config").then((r) => {
      setJastiperFeePct(Number(r.data?.jastiper_platform_fee_percent || 0));
    }).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    api.get("/catalog").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  const startNew = () => {
    setForm(empty);
    setEditing("new");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEdit = (it) => {
    // Backward-compat: older items only have `photo_path`
    const existing = Array.isArray(it.photos) && it.photos.length ? it.photos : (it.photo_path ? [it.photo_path] : []);
    setForm({
      name: it.name,
      description: it.description || "",
      price: it.price,
      country: it.country || "",
      photos: existing,
      fee_type: it.fee_type || "fixed",
      fee_value: it.fee_value ?? "",
      hide_price: !!it.hide_price,
    });
    setEditing(it.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancel = () => {
    setEditing(null);
    setForm(empty);
    setCostPrice("");
    setShowProfitCalc(false);
  };

  const save = async () => {
    if (!form.name || !form.price) {
      toast.error("Name and price are required");
      return;
    }
    const payload = {
      ...form,
      price: parseFloat(form.price),
      fee_value: form.fee_value === "" ? 0 : parseFloat(form.fee_value),
      photo_path: form.photos?.[0] || null,  // legacy mirror
    };
    try {
      if (editing === "new") {
        await api.post("/catalog", payload);
        toast.success("Item added");
      } else {
        await api.put(`/catalog/${editing}`, payload);
        toast.success("Item updated");
      }
      cancel();
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this catalog item?")) return;
    try {
      await api.delete(`/catalog/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-8" data-testid="catalog-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <div className="label-caps text-zinc-500 mb-2">{t("catalog.label")}</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">{t("catalog.title")}</h1>
          <p className="text-zinc-600 mt-2">{t("catalog.desc")}</p>
        </div>
        <button onClick={startNew} className="btn-primary-flat self-start sm:self-auto" data-testid="add-item-btn">
          <Plus size={16} strokeWidth={1.5} /> {t("catalog.add_item")}
        </button>
      </div>

      {editing && (
        <div className="border border-[#002FA7] bg-white p-4 sm:p-6 space-y-4" data-testid="catalog-edit-form">
          <div className="flex justify-between items-center">
            <div className="font-display text-lg sm:text-xl tracking-tight">{editing === "new" ? t("catalog.add_to_catalog") : t("catalog.edit_item")}</div>
            <button onClick={cancel} data-testid="cancel-edit-btn"><X size={16} strokeWidth={1.5} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <input className="input-flat" placeholder={t("catalog.name_placeholder")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name-input" />
            <input type="number" className="input-flat" placeholder={t("catalog.price_placeholder")} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="item-price-input" />
            <select className="input-flat" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="item-country-input">
              <option value="">{t("catalog.country_placeholder")}</option>
              {countries.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
            </select>
            <input className="input-flat" placeholder={t("catalog.desc_placeholder")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="item-desc-input" />
          </div>
          <MultiImageUploader value={form.photos} onChange={(arr) => setForm({ ...form, photos: arr })} testid="item-photos" />

          {/* Jastiper Fee — cuan per item (ditambahkan ke price, tampil ke buyer sebagai harga final) */}
          <div className="border-t pt-5 space-y-3" style={{borderColor: "var(--app-border)"}} data-testid="fee-section">
            <div>
              <div className="label-caps" style={{color: "var(--app-muted)"}}>{t("catalog.fee_label")}</div>
              <p className="text-xs mt-1" style={{color: "var(--app-muted)"}}>
                {t("catalog.fee_desc")}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-0 border" style={{borderColor: "var(--app-border)"}} data-testid="fee-type-toggle">
              <button
                type="button"
                onClick={() => setForm({ ...form, fee_type: "fixed" })}
                className="p-3 text-sm transition-colors"
                style={{
                  borderRight: "1px solid var(--app-border)",
                  backgroundColor: form.fee_type === "fixed" ? "var(--app-brand)" : "transparent",
                  color: form.fee_type === "fixed" ? "#fff" : "var(--app-fg)",
                }}
                data-testid="fee-type-fixed-btn"
              >
                {t("catalog.fee_fixed")}
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, fee_type: "percent" })}
                className="p-3 text-sm transition-colors"
                style={{
                  backgroundColor: form.fee_type === "percent" ? "var(--app-brand)" : "transparent",
                  color: form.fee_type === "percent" ? "#fff" : "var(--app-fg)",
                }}
                data-testid="fee-type-percent-btn"
              >
                {t("catalog.fee_percent")}
              </button>
            </div>
            <div className="relative max-w-xs">
              <input
                type="number"
                step={form.fee_type === "percent" ? "0.1" : "1000"}
                min="0"
                className="input-flat pr-16 font-mono"
                placeholder={form.fee_type === "percent" ? t("catalog.fee_percent_placeholder") : t("catalog.fee_fixed_placeholder")}
                value={form.fee_value}
                onChange={(e) => setForm({ ...form, fee_value: e.target.value })}
                data-testid="fee-value-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-sm" style={{color: "var(--app-muted)"}}>
                {form.fee_type === "percent" ? "%" : "IDR"}
              </span>
            </div>
            {form.price && form.fee_value > 0 && (() => {
              const basePrice = parseFloat(form.price) || 0;
              const feeAmount = form.fee_type === "percent"
                ? basePrice * (parseFloat(form.fee_value) || 0) / 100
                : (parseFloat(form.fee_value) || 0);
              const finalPrice = basePrice + feeAmount;
              return (
                <div className="p-3 font-mono text-sm space-y-1" style={{backgroundColor: "var(--app-surface-alt)", border: "1px solid var(--app-border)"}} data-testid="final-price-preview">
                  <div className="flex justify-between" style={{color: "var(--app-muted)"}}>
                    <span>{t("catalog.base_price_row")}</span>
                    <span>{formatIDR(basePrice)}</span>
                  </div>
                  <div className="flex justify-between" style={{color: "var(--app-muted)"}}>
                    <span>{t("catalog.plus_jastiper_fee")}</span>
                    <span>+ {formatIDR(feeAmount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t pt-1 mt-1" style={{borderColor: "var(--app-border)", color: "var(--app-brand)"}}>
                    <span>{t("catalog.final_price_row")}</span>
                    <span>{formatIDR(finalPrice)}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Profit Calculator — local only, NEVER saved to backend (jastiper's rahasia dapur) */}
          <div className="border border-dashed p-4 space-y-3" style={{borderColor: "var(--app-border)"}} data-testid="profit-calc-section">
            <button
              type="button"
              onClick={() => setShowProfitCalc((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity w-full text-left"
              data-testid="profit-calc-toggle"
            >
              <Calculator size={16} strokeWidth={1.5} />
              <span>{t("catalog.profit_calc")} <span className="font-mono text-xs opacity-60">{t("catalog.profit_calc_hint")}</span></span>
              <span className="ml-auto text-xs opacity-60">{showProfitCalc ? "−" : "+"}</span>
            </button>

            {showProfitCalc && (() => {
              const cost = parseFloat(costPrice) || 0;
              const sell = parseFloat(form.price) || 0;
              const profit = sell - cost;
              const margin = sell > 0 ? (profit / sell) * 100 : 0;
              const markup = cost > 0 ? (profit / cost) * 100 : 0;
              const positive = profit >= 0;
              return (
                <div className="space-y-3 pt-1" data-testid="profit-calc-body">
                  <p className="text-xs" style={{color: "var(--app-muted)"}}>
                    {t("catalog.profit_calc_desc")}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="label-caps mb-1 block" style={{color: "var(--app-muted)"}}>{t("catalog.cost_label")}</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="1000"
                          className="input-flat font-mono pr-12"
                          placeholder={t("catalog.cost_placeholder")}
                          value={costPrice}
                          onChange={(e) => setCostPrice(e.target.value)}
                          data-testid="profit-cost-input"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs" style={{color: "var(--app-muted)"}}>IDR</span>
                      </div>
                    </div>
                    <div>
                      <label className="label-caps mb-1 block" style={{color: "var(--app-muted)"}}>{t("catalog.sell_label")}</label>
                      <div className="input-flat font-mono flex items-center justify-between" data-testid="profit-sell-display">
                        <span>{sell > 0 ? formatIDR(sell) : "—"}</span>
                        <span className="text-xs" style={{color: "var(--app-muted)"}}>IDR</span>
                      </div>
                    </div>
                  </div>

                  {(cost > 0 && sell > 0) && (
                    <div
                      className="p-3 font-mono text-sm space-y-1.5"
                      style={{backgroundColor: positive ? "var(--app-brand-soft)" : "#FEE2E2", border: `1px solid ${positive ? "var(--app-brand)" : "#FCA5A5"}`}}
                      data-testid="profit-calc-result"
                    >
                      <div className="flex justify-between">
                        <span style={{color: "var(--app-muted)"}}>{t("catalog.profit_amount")}</span>
                        <span data-testid="profit-amount" style={{color: positive ? "var(--app-brand)" : "#B91C1C", fontWeight: 600}}>
                          {positive ? "+" : ""}{formatIDR(profit)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span style={{color: "var(--app-muted)"}}>{t("catalog.profit_margin")}</span>
                        <span data-testid="profit-margin">{margin.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span style={{color: "var(--app-muted)"}}>{t("catalog.profit_markup")}</span>
                        <span data-testid="profit-markup">{markup.toFixed(1)}%</span>
                      </div>
                      <div className="border-t pt-1.5 mt-1.5 text-[10px] flex items-center gap-1" style={{borderColor: positive ? "var(--app-brand)" : "#FCA5A5", color: "var(--app-muted)"}}>
                        <TrendingUp size={10} strokeWidth={1.5} />
                        <span>{t("catalog.after_platform_fee", { pct: jastiperFeePct, amount: formatIDR(sell * (1 - jastiperFeePct / 100)) })}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Privacy toggle */}
          <div className="border-2 p-4 space-y-3" style={{borderColor: form.hide_price ? "var(--app-brand)" : "var(--app-border)", backgroundColor: form.hide_price ? "var(--app-brand-soft)" : "transparent"}} data-testid="hide-price-section">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, hide_price: !form.hide_price })}
                role="switch"
                aria-checked={form.hide_price}
                className="relative inline-flex h-6 w-11 flex-shrink-0 mt-0.5 transition-colors"
                style={{backgroundColor: form.hide_price ? "var(--app-brand)" : "#d4d4d8"}}
                data-testid="hide-price-toggle"
              >
                <span
                  className="absolute top-0.5 inline-block h-5 w-5 bg-white shadow transition-transform"
                  style={{transform: form.hide_price ? "translateX(22px)" : "translateX(2px)"}}
                />
              </button>
              <div className="flex-1">
                <label className="font-medium text-sm flex items-center gap-2 cursor-pointer" onClick={() => setForm({ ...form, hide_price: !form.hide_price })}>
                  {form.hide_price ? <Lock size={14} strokeWidth={2} /> : <Eye size={14} strokeWidth={2} />}
                  {form.hide_price ? t("catalog.hide_price_on") : t("catalog.hide_price_off")}
                </label>
                <p className="text-xs mt-1" style={{color: "var(--app-muted)"}}>
                  {form.hide_price ? t("catalog.hide_price_on_desc") : t("catalog.hide_price_off_desc")}
                </p>
              </div>
            </div>
          </div>

          <button onClick={save} className="btn-primary-flat w-full sm:w-auto" data-testid="save-item-btn">
            <Check size={16} strokeWidth={1.5} /> {editing === "new" ? t("catalog.add_item") : t("catalog.save_changes")}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 text-zinc-500">{t("common.loading")}</div>
      ) : items.length === 0 && !editing ? (
        <div className="border border-zinc-200 bg-white py-20 text-center" data-testid="empty-catalog">
          <Package size={32} strokeWidth={1.5} className="mx-auto text-zinc-400 mb-3" />
          <p className="text-zinc-500 mb-4">{t("catalog.your_catalog_empty")}</p>
          <button onClick={startNew} className="btn-primary-flat" data-testid="empty-add-item-btn">
            <Plus size={16} strokeWidth={1.5} /> {t("catalog.add_your_first_item")}
          </button>
        </div>
      ) : (
        <>
          {(() => {
            // Build the per-country count map once; reused for both filter chips and grid.
            const counts = items.reduce((acc, it) => {
              const k = (it.country || "").trim() || "—";
              acc[k] = (acc[k] || 0) + 1;
              return acc;
            }, {});
            const countryList = Object.keys(counts).sort();
            const filtered = !countryFilter
              ? items
              : items.filter((it) => ((it.country || "").trim() || "—") === countryFilter);

            return (
              <>
                {countryList.length > 1 && (
                  <div
                    className="flex flex-wrap items-center gap-2 mb-5 pb-4 border-b"
                    style={{borderColor: "var(--app-border)"}}
                    data-testid="catalog-country-filter-bar"
                  >
                    <span className="text-xs font-mono uppercase tracking-wider mr-1" style={{color: "var(--app-muted)"}}>
                      {t("catalog.country_filter")}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCountryFilter("")}
                      className="px-3 py-1.5 text-xs font-medium border transition-colors"
                      style={{
                        borderColor: countryFilter === "" ? "var(--app-brand)" : "var(--app-border)",
                        backgroundColor: countryFilter === "" ? "var(--app-brand)" : "transparent",
                        color: countryFilter === "" ? "white" : "var(--app-fg)",
                      }}
                      data-testid="catalog-filter-all"
                    >
                      {t("catalog.all_count", { count: items.length })}
                    </button>
                    {countryList.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCountryFilter(c)}
                        className="px-3 py-1.5 text-xs font-medium border transition-colors"
                        style={{
                          borderColor: countryFilter === c ? "var(--app-brand)" : "var(--app-border)",
                          backgroundColor: countryFilter === c ? "var(--app-brand)" : "transparent",
                          color: countryFilter === c ? "white" : "var(--app-fg)",
                        }}
                        data-testid={`catalog-filter-${c}`}
                      >
                        {c === "—" ? t("catalog.no_country_chip") : c} ({counts[c]})
                      </button>
                    ))}
                  </div>
                )}

                {filtered.length === 0 ? (
                  <div className="border border-dashed p-12 text-center text-sm" style={{borderColor: "var(--app-border)", color: "var(--app-muted)"}} data-testid="catalog-filter-empty">
                    {t("catalog.no_items_for")} <strong>{countryFilter}</strong>.
                    <button onClick={() => setCountryFilter("")} className="block mx-auto mt-2 text-xs underline" style={{color: "var(--app-brand)"}}>
                      {t("catalog.show_all")}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 stagger-fade" data-testid="catalog-grid">
                    {filtered.map((it) => {
                      const photos = (Array.isArray(it.photos) && it.photos.length) ? it.photos : (it.photo_path ? [it.photo_path] : []);
                      const mainPhoto = photos[0];
                      return (
            <div key={it.id} className="grid-card group" data-testid={`my-catalog-item-${it.id}`}>
              <div className="aspect-square overflow-hidden relative" style={{backgroundColor: "var(--app-surface-alt)"}}>
                {mainPhoto ? <img src={fileUrl(mainPhoto)} alt={it.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{color: "var(--app-muted)"}}><Package size={40} strokeWidth={1} /></div>}
                {photos.length > 1 && (
                  <div
                    className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-white rounded-sm"
                    style={{backgroundColor: "rgba(0,0,0,0.7)"}}
                    data-testid={`item-photo-count-${it.id}`}
                  >
                    +{photos.length - 1}
                  </div>
                )}
                {it.hide_price && (
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-white" style={{backgroundColor: "var(--app-brand)"}} data-testid={`item-private-badge-${it.id}`}>
                    <Lock size={10} strokeWidth={2} /> {t("catalog.private_badge")}
                  </div>
                )}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <button onClick={() => startEdit(it)} className="w-7 h-7 bg-white border border-zinc-200 flex items-center justify-center hover:bg-[#002FA7] hover:text-white" data-testid={`edit-item-${it.id}`}>
                    <Edit2 size={12} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => remove(it.id)} className="w-7 h-7 bg-white border border-zinc-200 flex items-center justify-center hover:bg-red-600 hover:text-white" data-testid={`delete-item-${it.id}`}>
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <div className="font-medium text-sm truncate">{it.name}</div>
                {(() => {
                  const fv = parseFloat(it.fee_value || 0);
                  const fee = fv > 0 ? (it.fee_type === "percent" ? parseFloat(it.price) * fv / 100 : fv) : 0;
                  const finalPrice = parseFloat(it.price) + fee;
                  return (
                    <>
                      <div className="font-mono text-sm text-[#002FA7] mt-1">{formatIDR(finalPrice)}</div>
                      {fee > 0 && (
                        <div className="text-[10px] text-zinc-500 font-mono">
                          base {formatIDR(it.price)} + fee {it.fee_type === "percent" ? `${fv}%` : formatIDR(fv)}
                        </div>
                      )}
                    </>
                  );
                })()}
                {it.country && <div className="text-xs text-zinc-500 mt-1">{it.country}</div>}
              </div>
            </div>
            );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
